/**
 * Per-cell and per-row error tooltips for the import-review table.
 *
 * Built on top of the shared `Tooltip` primitive
 * (`src/rescue/components/Tooltip/Tooltip.tsx`) which provides the portal,
 * positioning, and scroll/Escape bookkeeping. This module supplies the
 * red error theme + the validation-error list rendering on top.
 *
 * `aria-describedby` wires the trigger to the tooltip so screen readers
 * announce the error message on hover/focus.
 */

import React from 'react';
import Tooltip, { type TooltipPos, useTooltipState } from '../../../components/Tooltip/Tooltip';
import { CheckIcon, ErrorCircleIcon } from './icons';
import type { ValidationError } from './validateRow';
import styles from '../Integrations.module.scss';

/**
 * Minimum shape consumed by `ErrorTooltipPortal` / `StatusPill` — just a
 * human-readable message. `ValidationError` already satisfies this (it
 * has a `message` field), so the per-cell validation tooltips keep
 * working unchanged.
 */
export interface TooltipError {
  message: string;
}

interface ErrorTooltipPortalProps {
  errors: ReadonlyArray<TooltipError>;
  pos: TooltipPos;
  id?: string;
  /**
   * Optional header shown above the error list. Used by the row-level
   * `StatusPill` ("Errors Detected In Row") but omitted by per-cell
   * tooltips, where the header would be misleading — those tooltips
   * scope a single field's issues, not the whole row's.
   */
  title?: string;
}

export function ErrorTooltipPortal({ errors, pos, id, title }: ErrorTooltipPortalProps) {
  // Single-error tooltip: drop the header + bullet icon. They read as
  // boilerplate when there's only one message to convey.
  const showHeader = errors.length > 1 && !!title;
  const showIcons = errors.length > 1;
  return (
    <Tooltip pos={pos} id={id} className={styles.statusTooltip}>
      {showHeader && <div className={styles.statusTooltipTitle}>{title}</div>}
      {errors.map((err, i) => (
        // Index-keyed: tooltip lifetime is bound to the hover, errors
        // never reorder, and the looser `TooltipError` shape doesn't
        // carry a field discriminator to key on.
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className={styles.statusTooltipLine}>
          {showIcons && (
            <span className={styles.statusTooltipIcon}>
              <ErrorCircleIcon />
            </span>
          )}
          <span>{err.message}</span>
        </div>
      ))}
    </Tooltip>
  );
}

interface ErrorTooltipCellProps {
  errors: ValidationError[];
  className?: string;
  rowIndex: number;
  field: string;
  children: React.ReactNode;
}

/**
 * A `<td>` that surfaces a portal-rendered error tooltip on hover when
 * `errors` is non-empty. Mouse-hover only; the cell is not focusable
 * because `<td>` isn't a conventional tab stop and adding `tabIndex` to
 * every error cell would make the table needlessly tab-noisy. Keyboard
 * users get the same information via the row-level `StatusPill`, which
 * IS focusable and shows the row's full error list.
 */
export function ErrorTooltipCell({ errors, className, rowIndex, field, children }: ErrorTooltipCellProps) {
  const hasErrors = errors.length > 0;
  const tooltipId = `import-cell-tip-${rowIndex}-${field}`;
  const { triggerRef, open, pos, handleEnter, handleLeave } = useTooltipState<HTMLTableCellElement>(hasErrors);

  return (
    <td
      ref={triggerRef}
      className={className}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      aria-describedby={hasErrors && open ? tooltipId : undefined}
    >
      {children}
      {hasErrors && open && <ErrorTooltipPortal errors={errors} pos={pos} id={tooltipId} />}
    </td>
  );
}

interface StatusPillProps {
  isError: boolean;
  label: string;
  errors: ReadonlyArray<TooltipError>;
  rowIndex: number;
}

/**
 * Status pill at the end of each preview-table row. Shows a green check
 * + "Valid" when clean, or a red error icon + short label when not.
 * Hovering an error pill reveals the full list of issues for the row.
 */
export function StatusPill({ isError, label, errors, rowIndex }: StatusPillProps) {
  const tooltipId = `import-row-tip-${rowIndex}`;
  const { triggerRef, open, pos, handleEnter, handleLeave } = useTooltipState<HTMLSpanElement>(isError);
  return (
    <span
      ref={triggerRef}
      className={`${styles.reviewStatus} ${isError ? styles.reviewStatusErr : styles.reviewStatusOk}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      aria-describedby={isError && open ? tooltipId : undefined}
      tabIndex={isError ? 0 : -1}
    >
      {isError ? <ErrorCircleIcon /> : <CheckIcon />}
      {label}
      {isError && open && (
        <ErrorTooltipPortal errors={errors} pos={pos} id={tooltipId} title="Errors Detected In Row" />
      )}
    </span>
  );
}
